"use client";
import React, { useState, useMemo } from "react";
import Image from "next/image";
import { Typography, Popover } from "antd";
import PriceChange from "../PriceChange";
import classNames from "classnames";
import { CaretUpOutlined, CaretDownOutlined, FilterOutlined } from "@ant-design/icons";

export interface ScreenerData {
  symbol: string;
  price: number;
  priceChange: number;
  signal: string;
  logo?: string;
  technicalIndicators?: {
    rsi20?: number;
    ema200?: number;
    ma?: number;
  };
}

interface ScreenerTableProps {
  data: ScreenerData[];
  title: string;
  tag: {
    text: string;
    type: "hot" | "trending";
  };
}

type SortDirection = "asc" | "desc" | null;

const TechnicalIndicatorsPopover = ({ indicators }: { indicators?: ScreenerData['technicalIndicators'] }) => {
  if (!indicators) return null;
  
  return (
    <div className="flex flex-col gap-2 p-1">
      {indicators.rsi20 !== undefined && (
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-blue-500"></div>
          <Typography.Text>RSI20: {indicators.rsi20}</Typography.Text>
        </div>
      )}
      {indicators.ema200 !== undefined && (
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-orange-300"></div>
          <Typography.Text>EMA200: {indicators.ema200}</Typography.Text>
        </div>
      )}
      {indicators.ma !== undefined && (
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-cyan-400"></div>
          <Typography.Text>MA: {indicators.ma}</Typography.Text>
        </div>
      )}
    </div>
  );
};

const ScreenerTable: React.FC<ScreenerTableProps> = ({ data, title, tag }) => {
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  const handleSort = () => {
    setSortDirection(current => {
      if (current === null) return "asc";
      if (current === "asc") return "desc";
      return null;
    });
  };

  const sortedData = useMemo(() => {
    if (!sortDirection) return data;

    return [...data].sort((a, b) => {
      if (sortDirection === "asc") {
        return a.price - b.price;
      }
      return b.price - a.price;
    });
  }, [data, sortDirection]);

  return (
    <div className="w-full bg-white rounded-lg border border-gray-100 shadow-sm">
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <Typography.Text strong className="text-lg">
          {title}
        </Typography.Text>
        <div
          className={classNames(
            "px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1",
            tag.type === "hot" ? "bg-red-50 text-red-500" : "bg-blue-50 text-blue-500",
          )}
        >
          {tag.type === "trending" && (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M3 17L9 11L13 15L21 7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M14 7H21V14"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
          {tag.text}
        </div>
      </div>

      <div className="relative">
        <div className="sticky top-0 z-10 grid grid-cols-3 gap-4 mb-2 px-2 py-2 bg-gray-50 border border-gray-100">
          <Typography.Text className="text-gray-500 font-medium">TICKET</Typography.Text>
          <div className="flex items-center gap-1 cursor-pointer group" onClick={handleSort}>
            <Typography.Text className="text-gray-500 font-medium">PRICE</Typography.Text>
            <div className="flex flex-col">
              <CaretUpOutlined
                className={classNames(
                  "text-[10px]",
                  sortDirection === "asc" ? "text-blue-500" : "text-gray-400 group-hover:text-gray-600",
                )}
              />
              <CaretDownOutlined
                className={classNames(
                  "text-[10px] -mt-1",
                  sortDirection === "desc" ? "text-blue-500" : "text-gray-400 group-hover:text-gray-600",
                )}
              />
            </div>
          </div>
          <div className="flex items-center gap-1 cursor-pointer">
            <Typography.Text className="text-gray-500 font-medium">SIGNAL</Typography.Text>
            <FilterOutlined className="text-gray-400 hover:text-gray-600 text-xs" />
          </div>
        </div>

        <div className="space-y-[1px] max-h-[300px] overflow-y-auto">
          {sortedData.map((item, index) => (
            <Popover 
              key={`${item.symbol}-${index}`}
              content={<TechnicalIndicatorsPopover indicators={item.technicalIndicators} />}
              placement="right"
              mouseEnterDelay={0.3}
            >
              <div
                className="grid grid-cols-3 gap-4 px-2 py-3 hover:bg-gray-50 cursor-pointer transition-colors duration-200 border-b border-gray-100 last:border-b-0"
              >
                <div className="flex items-center gap-2">
                  {item.logo ? (
                    <Image src={item.logo} alt={item.symbol} width={24} height={24} className="rounded-full" />
                  ) : (
                    <div className="w-6 h-6 bg-gray-200 rounded-full" />
                  )}
                  <Typography.Text strong>{item.symbol}</Typography.Text>
                </div>
                <div className="flex items-center gap-1">
                  <Typography.Text>${item.price.toLocaleString()}</Typography.Text>
                  <Typography.Text className="text-gray-400">
                    (<PriceChange price={item.priceChange} />)
                  </Typography.Text>
                </div>
                <Typography.Text>{item.signal}</Typography.Text>
              </div>
            </Popover>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ScreenerTable;

